import React, { useEffect, useState } from 'react';
import { ShoppingBag, Save, RefreshCw, Check } from 'lucide-react';
import { api } from '@/lib/api';
import { TextField, Toggle, Select, FileField } from '@/components/admin/shared';

const BUTTON_STYLES = [
  { value: 'solid',    label: 'Solid' },
  { value: 'outline',  label: 'Outline' },
  { value: 'gold',     label: 'Gold accent' },
];

const TAX_MODES = [
  { value: 'none',      label: 'No tax' },
  { value: 'flat',      label: 'Flat percent (config)' },
  { value: 'stripe_tax', label: 'Delegate to Stripe Tax' },
];

export default function CheckoutSettingsManager() {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/checkout-settings');
      setForm(data);
    } catch (e) {
      setMsg(e?.response?.data?.detail || 'Failed to load checkout settings');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form) return;
    setSaving(true);
    try {
      const { data } = await api.put('/admin/checkout-settings', form);
      setForm(data);
      setMsg('Checkout settings saved.');
    } catch (e) {
      setMsg(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  if (loading || !form) {
    return <div className="p-4 text-xs font-mono text-[var(--pg-ink-faint)]">Loading checkout settings…</div>;
  }

  return (
    <div className="space-y-6" data-testid="checkout-settings-manager">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShoppingBag size={18} className="text-[var(--pg-gold-2)]" />
          <h3 className="font-serif text-lg text-[var(--pg-ink)]">Checkout Settings</h3>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="pg-btn-ghost px-3 py-2 rounded-[var(--pg-radius-sm)] text-xs flex items-center gap-2">
            <RefreshCw size={14} /> Reload
          </button>
          <button data-testid="checkout-settings-save" onClick={save} disabled={saving} className="pg-btn-primary px-3 py-2 rounded-[var(--pg-radius-sm)] text-xs flex items-center gap-2 disabled:opacity-60">
            <Save size={14} /> {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      {msg && (
        <div className="pg-input rounded-[var(--pg-radius-sm)] px-3 py-2 text-xs text-[var(--pg-ink)] flex items-center gap-2">
          <Check size={14} className="text-[var(--pg-emerald-2)]" /> {msg}
        </div>
      )}

      {/* Branding */}
      <section className="pg-admin-card rounded-[var(--pg-radius-md)] p-5 space-y-4">
        <h4 className="pg-label text-[10px]">Branding</h4>
        <FileField label="Checkout logo" kind="image" value={form.logo_url} onChange={set('logo_url')} accept="image/*" />
        <div className="grid grid-cols-2 gap-3">
          <TextField label="Accent color (hex or var)" value={form.accent_color} onChange={set('accent_color')} placeholder="#c9a24a" />
          <Select label="Button style" value={form.button_style || 'solid'} onChange={set('button_style')} options={BUTTON_STYLES} />
        </div>
      </section>

      {/* Required fields */}
      <section className="pg-admin-card rounded-[var(--pg-radius-md)] p-5 space-y-3">
        <h4 className="pg-label text-[10px]">Required Fields at Checkout</h4>
        <div className="grid grid-cols-2 gap-2">
          <Toggle label="Require name" value={form.require_name} onChange={set('require_name')} />
          <Toggle label="Require phone" value={form.require_phone} onChange={set('require_phone')} />
          <Toggle label="Require shipping address" value={form.require_shipping_address} onChange={set('require_shipping_address')} />
          <Toggle label="Require billing address" value={form.require_billing_address} onChange={set('require_billing_address')} />
          <Toggle label="Capture email/SMS preferences" value={form.capture_comms_preferences} onChange={set('capture_comms_preferences')} />
          <Toggle label="Allow guest checkout" value={form.guest_checkout_allowed} onChange={set('guest_checkout_allowed')} />
          <Toggle label="Require account" value={form.account_required} onChange={set('account_required')} />
          <Toggle label="Require terms acknowledgement" value={form.terms_ack_required} onChange={set('terms_ack_required')} />
        </div>
        {form.terms_ack_required && (
          <TextField label="Terms acknowledgement label" value={form.terms_ack_label} onChange={set('terms_ack_label')} />
        )}
        <TextField label="Order note prompt (optional)" value={form.order_note_prompt} onChange={set('order_note_prompt')} placeholder="Anything we should know?" />
      </section>

      {/* Post-purchase */}
      <section className="pg-admin-card rounded-[var(--pg-radius-md)] p-5 space-y-3">
        <h4 className="pg-label text-[10px]">Thank You Screen</h4>
        <TextField label="Headline" value={form.thank_you_headline} onChange={set('thank_you_headline')} />
        <TextField label="Body" value={form.thank_you_body} onChange={set('thank_you_body')} textarea rows={4} />
      </section>

      {/* Tax */}
      <section className="pg-admin-card rounded-[var(--pg-radius-md)] p-5 space-y-3">
        <h4 className="pg-label text-[10px]">Tax</h4>
        <div className="grid grid-cols-2 gap-3">
          <Select label="Tax mode" value={form.tax_mode || 'none'} onChange={set('tax_mode')} options={TAX_MODES} />
          <TextField label="Flat tax percent" type="number" value={form.tax_flat_percent} onChange={set('tax_flat_percent')} placeholder="0" />
        </div>
        <p className="text-[10px] font-mono text-[var(--pg-ink-faint)]">Stripe Tax integration is placeholder — no calculation runs until enabled.</p>
      </section>

      <div className="flex justify-end">
        <button data-testid="checkout-settings-save-bottom" onClick={save} disabled={saving} className="pg-btn-primary px-4 py-2 rounded-[var(--pg-radius-sm)] text-xs flex items-center gap-2 disabled:opacity-60">
          <Save size={14} /> {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </div>
  );
}
